import { Link } from "wouter";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { ArrowLeft, Smartphone, Shield, Zap, Bell, QrCode } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function MobileApps() {
  const { t } = useTranslation();

  const features = [
    {
      icon: Bell,
      title: "Instant Notifications",
      description: "Never miss a deal or a message. Get alerted instantly when verified sellers post items you want."
    },
    {
      icon: Smartphone,
      title: "Snap & Sell",
      description: "List items in seconds directly from your phone's camera. AI auto-fills the details for you."
    },
    {
      icon: Shield,
      title: "Secure Chat",
      description: "Negotiate safely with built-in messaging that keeps your personal contact info private."
    },
    {
      icon: Zap,
      title: "Location-Based",
      description: "Find hidden gems in your neighborhood with our precise geolocation features."
    }
  ];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <nav className="border-b border-border sticky top-0 z-50 bg-background/95 backdrop-blur">
        <div className="container flex items-center justify-between h-16">
          <Link href="/">
            <a className="text-2xl font-bold gradient-text">{t('common.appName')}</a>
          </Link>
          <div className="flex items-center gap-4">
            <Link href="/">
              <a className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
                <ArrowLeft className="w-4 h-4" />
                {t('common.back')}
              </a>
            </Link>
            <LanguageSwitcher />
          </div>
        </div>
      </nav>

      <main className="flex-1">
        {/* Hero */}
        <section className="py-12 md:py-24 bg-gradient-to-br from-primary/10 via-background to-secondary/10 overflow-hidden">
          <div className="container flex flex-col md:flex-row items-center gap-12">
            <div className="flex-1 space-y-8 text-center md:text-left">
              <h1 className="text-4xl md:text-6xl font-bold leading-tight">
                Get the <span className="gradient-text">Listo App</span>
              </h1>
              <p className="text-xl text-muted-foreground max-w-lg mx-auto md:mx-0">
                The fastest way to buy and sell locally. Available now for iOS and Android.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
                <Button size="lg" className="h-14 px-8 text-lg">
                  <span className="mr-2"></span> App Store
                </Button>
                <Button size="lg" variant="outline" className="h-14 px-8 text-lg">
                  <span className="mr-2">▶</span> Google Play
                </Button>
              </div>

              <div className="hidden md:flex items-center gap-4 text-sm text-muted-foreground pt-4">
                <div className="bg-white p-2 border rounded-lg shadow-sm">
                  <QrCode className="w-16 h-16 text-foreground" />
                </div>
                <div>
                  <p className="font-semibold text-foreground">Scan to download</p>
                  <p>Direct link for your device</p>
                </div>
              </div>
            </div>

            <div className="flex-1 relative">
              <div className="relative w-64 md:w-80 h-[500px] md:h-[600px] bg-foreground rounded-[3rem] border-8 border-foreground shadow-2xl mx-auto overflow-hidden">
                <div className="absolute top-0 w-full h-8 bg-black z-20 rounded-t-[2rem]">
                  <div className="w-24 h-6 bg-black bottom-0 absolute left-1/2 -translate-x-1/2 rounded-b-xl"></div>
                </div>
                <div className="w-full h-full bg-background relative pt-12 px-4">
                  {/* Mock App Interface */}
                  <div className="space-y-4">
                    <div className="h-40 bg-muted rounded-xl w-full animate-pulse"></div>
                    <div className="grid grid-cols-2 gap-2">
                       <div className="h-32 bg-secondary/20 rounded-xl w-full"></div>
                       <div className="h-32 bg-primary/20 rounded-xl w-full"></div>
                    </div>
                    <div className="h-12 bg-primary rounded-full w-2/3 mx-auto mt-8"></div>
                  </div>
                </div>
              </div>
              {/* Floating Badge */}
              <div className="absolute top-20 -right-4 md:right-10 bg-card p-4 rounded-xl shadow-xl border border-border animate-bounce duration-[3000ms]">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="font-bold">4.9 Star Rating</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-24 container">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((f, i) => (
              <div key={i} className="text-center p-6 bg-muted/30 rounded-xl">
                <div className="inline-flex p-3 bg-background rounded-full shadow-sm mb-4 text-primary">
                  <f.icon className="w-6 h-6" />
                </div>
                <h3 className="font-bold text-lg mb-2">{f.title}</h3>
                <p className="text-muted-foreground">{f.description}</p>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}
